import React, { useState, useEffect, useCallback } from 'react';
import { policyAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import { PdfViewer } from '../../components/ui/pdf-viewer';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { FileText, Upload, Trash2, Download, Plus, Eye, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getFileUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/uploads/')) return `${BACKEND_URL}/api${url}`;
  return `${BACKEND_URL}${url}`;
};

const CATEGORIES = [
  { value: 'general', label: 'General Policy' },
  { value: 'hr', label: 'HR Policy' },
  { value: 'rules', label: 'Office Rules' },
  { value: 'notice', label: 'Notice / Circular' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'other', label: 'Other' },
];

const Policies = () => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [viewingPolicy, setViewingPolicy] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    category: 'general',
    description: '',
    company: 'both',
    file: null,
  });

  const fetchPolicies = useCallback(async () => {
    try {
      const res = await policyAPI.getAll();
      setPolicies(res.data);
    } catch (e) {
      console.error('Failed to fetch policies', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPolicies();
  }, [fetchPolicies]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!formData.file || !formData.title) return;

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', formData.file);
      fd.append('title', formData.title);
      fd.append('category', formData.category);
      fd.append('description', formData.description);
      fd.append('company', formData.company);

      await policyAPI.upload(fd);
      await fetchPolicies();
      setIsUploadOpen(false);
      setFormData({ title: '', category: 'general', description: '', company: 'both', file: null });
    } catch (error) {
      console.error('Failed to upload policy:', error);
      alert(error.response?.data?.detail || 'Failed to upload policy');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await policyAPI.delete(deleteTarget.id);
      await fetchPolicies();
    } catch (e) {
      console.error('Failed to delete policy', e);
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleReupload = async (policyId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.doc,.docx,.png,.jpg,.jpeg,.txt';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const fd = new FormData();
      fd.append('file', file);
      try {
        const token = localStorage.getItem('token');
        const resp = await fetch(`${BACKEND_URL}/api/policies/${policyId}/reupload`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` },
          body: fd,
        });
        if (!resp.ok) throw new Error('Re-upload failed');
        await fetchPolicies();
        alert('File re-uploaded successfully!');
      } catch (err) {
        alert('Failed to re-upload file. Please try again.');
      }
    };
    input.click();
  };

  const getCategoryLabel = (cat) => {
    const found = CATEGORIES.find(c => c.value === cat);
    return found ? found.label : cat;
  };

  const getCategoryColor = (cat) => {
    const colors = {
      general: 'bg-blue-100 text-blue-700',
      hr: 'bg-green-100 text-green-700',
      rules: 'bg-red-100 text-red-700',
      notice: 'bg-amber-100 text-amber-700',
      compliance: 'bg-purple-100 text-purple-700',
      other: 'bg-slate-100 text-slate-700',
    };
    return colors[cat] || colors.other;
  };

  const getCompanyLabel = (company) => {
    switch (company) {
      case 'empact_studio': return 'Empact Studio';
      case 'event_resource': return 'Event Resource';
      case 'both': return 'Both Companies';
      default: return company;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1D35A0]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="policies-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-[#1D35A0]">Company Policies & Notices</h2>
          <p className="text-slate-500 text-sm mt-1">Upload and manage company policies, rules, and notices for all employees</p>
        </div>
        <Button
          onClick={() => setIsUploadOpen(true)}
          className="bg-[#1D35A0] hover:bg-[#152A80] text-white"
          data-testid="upload-policy-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Upload Policy
        </Button>
      </div>

      {/* Policy List */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        {policies.length === 0 ? (
          <div className="text-center py-16" data-testid="no-policies">
            <FileText className="w-8 h-8 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">No policies uploaded yet</p>
            <p className="text-slate-400 text-sm mt-1">Upload your first company policy or notice</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-200">
            {policies.map((policy) => (
              <div key={policy.id} className="p-4 hover:bg-slate-50 transition-colors" data-testid={`policy-item-${policy.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-medium text-slate-900 truncate">{policy.title}</h3>
                      {policy.description && (
                        <p className="text-sm text-slate-500 mt-0.5 line-clamp-2">{policy.description}</p>
                      )}
                      {!policy.storage_path && (
                        <div className="mt-1.5 flex items-center gap-2">
                          <span className="text-xs text-red-600 font-medium bg-red-50 px-2 py-0.5 rounded">File missing — please re-upload</span>
                          <button
                            onClick={() => handleReupload(policy.id)}
                            className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                            data-testid={`reupload-policy-${policy.id}`}
                          >
                            <RefreshCw className="w-3 h-3" /> Re-upload
                          </button>
                        </div>
                      )}
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(policy.category)}`}>
                          {getCategoryLabel(policy.category)}
                        </span>
                        <span className="text-xs text-slate-400">{getCompanyLabel(policy.company)}</span>
                        <span className="text-xs text-slate-400">
                          Uploaded {new Date(policy.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                        <span className="text-xs text-slate-400">by {policy.uploaded_by_name}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => setViewingPolicy(policy)}
                      className="inline-flex items-center justify-center rounded-md text-sm font-medium h-9 w-9 hover:bg-blue-50 transition-colors"
                      data-testid={`view-policy-${policy.id}`}
                      title="View in-app"
                    >
                      <Eye className="w-4 h-4 text-blue-600" />
                    </button>
                    <button
                      onClick={() => handleReupload(policy.id)}
                      className="inline-flex items-center justify-center rounded-md text-sm font-medium h-9 w-9 hover:bg-amber-50 transition-colors"
                      data-testid={`reupload-btn-${policy.id}`}
                      title="Re-upload file"
                    >
                      <RefreshCw className="w-4 h-4 text-amber-600" />
                    </button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteTarget(policy)}
                      className="text-red-500 hover:text-red-700 h-9 w-9 p-0"
                      data-testid={`delete-policy-${policy.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upload Dialog */}
      <Dialog open={isUploadOpen} onOpenChange={setIsUploadOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload Policy / Notice</DialogTitle>
            <DialogDescription>Upload a company policy, rule, or notice document</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="policy-title">Title *</Label>
              <Input
                id="policy-title"
                value={formData.title}
                onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))}
                placeholder="e.g. Leave Policy 2025"
                required
                data-testid="input-policy-title"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData(p => ({ ...p, category: v }))}>
                  <SelectTrigger data-testid="select-policy-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Applicable To</Label>
                <Select value={formData.company} onValueChange={(v) => setFormData(p => ({ ...p, company: v }))}>
                  <SelectTrigger data-testid="select-policy-company">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="both">Both Companies</SelectItem>
                    <SelectItem value="empact_studio">Empact Studio</SelectItem>
                    <SelectItem value="event_resource">Event Resource</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="policy-desc">Description (optional)</Label>
              <Textarea
                id="policy-desc"
                value={formData.description}
                onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                placeholder="Brief description of this policy..."
                rows={2}
                data-testid="input-policy-description"
              />
            </div>
            <div className="space-y-2">
              <Label>Document *</Label>
              <label className="cursor-pointer block">
                <div className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-slate-300 rounded-lg hover:border-blue-400 hover:bg-blue-50/50 transition-colors">
                  <Upload className="w-5 h-5 text-slate-400" />
                  <span className="text-sm text-slate-600">
                    {formData.file ? formData.file.name : 'Click to select file (PDF, DOC, DOCX, TXT, JPG, PNG)'}
                  </span>
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                  onChange={(e) => setFormData(p => ({ ...p, file: e.target.files[0] }))}
                  data-testid="input-policy-file"
                />
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setIsUploadOpen(false)}>Cancel</Button>
              <Button
                type="submit"
                className="bg-[#1D35A0] hover:bg-[#152A80] text-white"
                disabled={uploading || !formData.file || !formData.title}
                data-testid="submit-policy-btn"
              >
                {uploading ? 'Uploading...' : 'Upload Policy'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Policy</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteTarget?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-policy">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Policy Viewer Dialog */}
      {viewingPolicy && (
        <Dialog open={!!viewingPolicy} onOpenChange={() => setViewingPolicy(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh]" data-testid="policy-viewer-dialog">
            <DialogHeader>
              <DialogTitle>{viewingPolicy.title}</DialogTitle>
              <DialogDescription>{viewingPolicy.description || 'Company Policy Document'}</DialogDescription>
            </DialogHeader>
            <PdfViewer
              policyId={viewingPolicy.id}
              title={viewingPolicy.title}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Policies;
